// PWA ホーム画面追加バナーの状態管理。
//
// 仕様:
//   - localStorage キー
//       'install-banner-dismissed': 閉じた時刻 (Date.now() の数値文字列)
//       'last-visit'              : 最終訪問時刻
//   - 起動時に last-visit から 7 日以上経過していたら dismissed を消して再表示。
//   - iOS は beforeinstallprompt が発火しないので、navigator.standalone 等で
//     未インストールを判定して案内文のみ表示する。
//   - Android は beforeinstallprompt を preventDefault で保管し、
//     「インストール」ボタンで .prompt() を呼ぶ。userChoice 完了 / 閉じた時に
//     dismissed へ Date.now() を保存する。
//
// 注意:
//   - beforeinstallprompt の event listener は **モジュール読み込み時に登録** する。
//     React アプリのマウント前に発火すると取りこぼすため、import 副作用として早期に登録。

import { useCallback, useEffect, useState } from 'react';

const STORAGE_DISMISSED = 'install-banner-dismissed';
const STORAGE_LAST_VISIT = 'last-visit';
const REVISIT_RESET_MS = 7 * 24 * 60 * 60 * 1000;

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();

function notifyListeners(): void {
  for (const f of listeners) f();
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
    notifyListeners();
  });
  window.addEventListener('appinstalled', () => {
    // インストール完了後はもう案内不要。dismissed に印をつけて以後表示しない。
    deferredPrompt = null;
    safeSet(STORAGE_DISMISSED, String(Date.now()));
    notifyListeners();
  });
}

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* localStorage 不可（プライベートモード等）。サイレントに諦める */
  }
}

function safeRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* 同上 */
  }
}

/**
 * 起動時に呼ぶ: 前回訪問から 7 日以上空いていたら dismissed を消去する。
 * 同時に last-visit を現在時刻で更新する。
 * main.tsx から React マウント前に同期実行する。
 */
export function evaluateRevisitPolicy(now: number = Date.now()): void {
  const lastVisitRaw = safeGet(STORAGE_LAST_VISIT);
  if (lastVisitRaw) {
    const lastVisit = Number(lastVisitRaw);
    if (Number.isFinite(lastVisit) && now - lastVisit > REVISIT_RESET_MS) {
      safeRemove(STORAGE_DISMISSED);
    }
  }
  safeSet(STORAGE_LAST_VISIT, String(now));
}

function readDismissed(): boolean {
  return Boolean(safeGet(STORAGE_DISMISSED));
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  // iOS Safari (非標準)
  const nav = navigator as Navigator & { standalone?: boolean };
  if (nav.standalone === true) return true;
  // Android Chrome / Edge / その他
  return window.matchMedia?.('(display-mode: standalone)').matches ?? false;
}

function detectPlatform(): 'ios' | 'android' | 'other' {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent;
  // iPadOS 13+ は UA が Mac になるので maxTouchPoints で iPad を判別
  const isIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  if (isIOS) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'other';
}

export interface InstallPromptState {
  platform: 'ios' | 'android' | 'other';
  isInstalled: boolean;
  isDismissed: boolean;
  /** Android で beforeinstallprompt を保管済みなら true */
  canPrompt: boolean;
  /** Android のみ: ネイティブインストールダイアログを表示 */
  promptInstall: () => Promise<void>;
  /** ユーザが ✕ を押した、もしくは案内を閉じた */
  dismiss: () => void;
}

/**
 * バナー UI 用フック。beforeinstallprompt の到着・appinstalled 時に
 * 自動で再評価して再レンダリングする。
 */
export function useInstallPrompt(): InstallPromptState {
  // singleton の更新時に再レンダリングさせるためのバージョン
  const [, setVersion] = useState(0);

  useEffect(() => {
    const f = (): void => setVersion((v) => v + 1);
    listeners.add(f);
    return () => {
      listeners.delete(f);
    };
  }, []);

  const platform = detectPlatform();
  const installed = isStandalone();
  const dismissed = readDismissed();
  const canPrompt = deferredPrompt !== null;

  const promptInstall = useCallback(async (): Promise<void> => {
    const ev = deferredPrompt;
    if (!ev) return;
    try {
      await ev.prompt();
      await ev.userChoice; // accepted / dismissed どちらでも以後再表示しない
    } finally {
      deferredPrompt = null;
      safeSet(STORAGE_DISMISSED, String(Date.now()));
      notifyListeners();
      setVersion((v) => v + 1);
    }
  }, []);

  const dismiss = useCallback((): void => {
    safeSet(STORAGE_DISMISSED, String(Date.now()));
    setVersion((v) => v + 1);
  }, []);

  return {
    platform,
    isInstalled: installed,
    isDismissed: dismissed,
    canPrompt,
    promptInstall,
    dismiss,
  };
}
